import React from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  message,
  Space,
  Typography,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useMarkAttendanceMutation } from "../../redux/services/attendance";
import { useSelector } from "react-redux";
import "./AttendanceCard.less";

const AttendanceCard = (props) => {
  const [markAttendance] = useMarkAttendanceMutation();
  const [loading, setLoading] = React.useState(false);
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const { student_id, currentClassId } = useSelector((state) => state.auth);

  const handleAttendanceModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = async (status) => {
    setLoading(true);
    try {
      const payload = {
        student_assoc_id: student_id,
        class_assoc_id: currentClassId,
        status: status,
      };

      // Call the markAttendance mutation and properly unwrap the response
      const response = await markAttendance(payload).unwrap();

      // Close modal
      setIsModalVisible(false);

      // Check if the API call was successful
      if (response.status_code === 200) {
        // Show success message
        message.success({
          content: response.message || "Attendance marked successfully!",
          duration: 3,
        });

        // Refresh attendance data if refetch function was provided
        if (props.refetchAttendanceByCoaching) {
          props.refetchAttendanceByCoaching();
        }
      } else {
        // Show error message if status code is not 200
        message.error({
          content: response.message || "Failed to mark attendance.",
          duration: 3,
        });
      }
    } catch (error) {
      // Close modal on error
      setIsModalVisible(false);

      // Show appropriate error message
      message.error({
        content:
          error?.data?.message ||
          "Failed to mark attendance. Please try again.",
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <Card className='attendance-card' loading={props.loading}>
      <Row justify='space-between' className='logo-and-location'>
        <div className='center-img'>
          <img src={props.centrelogo} alt='institute logo' className='logo' />
        </div>
        <div className='location'>
          <svg
            width='15'
            height='15'
            viewBox='0 0 15 15'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g id='mingcute:location-fill' clipPath='url(#clip0_437_2550)'>
              <g id='Group'>
                <path
                  id='Vector'
                  d='M7.5 1.25C8.99184 1.25 10.4226 1.84263 11.4775 2.89752C12.5324 3.95242 13.125 5.38316 13.125 6.875C13.125 8.79625 12.0775 10.3688 10.9738 11.4969C10.4223 12.0544 9.82058 12.5599 9.17625 13.0069L8.91 13.1881L8.785 13.2713L8.54938 13.4213L8.33938 13.5494L8.07938 13.7006C7.9029 13.8014 7.7032 13.8543 7.5 13.8543C7.2968 13.8543 7.0971 13.8014 6.92062 13.7006L6.66062 13.5494L6.33563 13.3494L6.21562 13.2713L5.95937 13.1006C5.26427 12.6303 4.61681 12.0932 4.02625 11.4969C2.9225 10.3681 1.875 8.79625 1.875 6.875C1.875 5.38316 2.46763 3.95242 3.52252 2.89752C4.57742 1.84263 6.00816 1.25 7.5 1.25ZM7.5 5C7.25377 5 7.00995 5.0485 6.78247 5.14273C6.55498 5.23695 6.34828 5.37507 6.17417 5.54917C6.00006 5.72328 5.86195 5.92998 5.76773 6.15747C5.6735 6.38495 5.625 6.62877 5.625 6.875C5.625 7.12123 5.6735 7.36505 5.76773 7.59253C5.86195 7.82002 6.00006 8.02672 6.17417 8.20083C6.34828 8.37494 6.55498 8.51305 6.78247 8.60728C7.00995 8.7015 7.25377 8.75 7.5 8.75C7.99728 8.75 8.47419 8.55246 8.82583 8.20083C9.17746 7.8492 9.375 7.37228 9.375 6.875C9.375 6.37772 9.17746 5.90081 8.82583 5.54917C8.47419 5.19754 7.99728 5 7.5 5Z'
                  fill='black'
                />
              </g>
            </g>
            <defs>
              <clipPath id='clip0_437_2550'>
                <rect width='15' height='15' fill='white' />
              </clipPath>
            </defs>
          </svg>
          {props.location}
        </div>
      </Row>

      <Col span={24} className='attendance-details'>
        <Row>
          <Col span={12}>
            <div className='subject-title'>Class name</div>
            <div className='subject-name'>{props.classname}</div>
          </Col>
          <Col span={12}>
            <div className='teacher-title'>Teacher's name</div>
            <div className='teacher-name'>{props.teachername}</div>
          </Col>
        </Row>
      </Col>

      <Col span={24} className='attendance-footer'>
        <Row>
          <Col span={12}>
            <div className='timing-title'>Timing</div>
            <div className='timing'>{props.timing}</div>
          </Col>
          <Col span={12} className='attendance-button'>
            <Button type='primary' onClick={handleAttendanceModal}>
              Mark Attendance
            </Button>
          </Col>
        </Row>
      </Col>

      {/* Attendance Modal */}
      <Modal
        title='Mark Attendance'
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        centered
        closable={true}
        className='attendance-modal'
      >
        <Space direction='vertical' size='middle'>
          <div className='info'>
            <InfoCircleOutlined className='info-icon' />
            <span style={{ color: "#1890ff" }}>
              Make sure you are in the {props.classname} centre to mark present
            </span>
          </div>

          <div className='button-group'>
            <Button
              type='primary'
              block
              className='present-button'
              icon={
                <span role='img' aria-label='hand'>
                  🖐️
                </span>
              }
              onClick={() => handleOk("PRESENT")}
              loading={loading}
            >
              Present
            </Button>
            <Button
              type='default'
              danger
              block
              className='absent-button'
              icon={
                <span role='img' aria-label='cross'>
                  ❌
                </span>
              }
              onClick={() => handleOk("ABSENT")}
              loading={loading}
            >
              Absent
            </Button>
          </div>
        </Space>
      </Modal>
    </Card>
  );
};

export default AttendanceCard;
